import { Col, Form, FormGroup, Row } from 'react-bootstrap';
import React, { ChangeEvent, Fragment, useState } from 'react';
import IActivity from 'interfaces/IActivity';

interface IActivityEntry {
    activity?: IActivity;
    index?: number;
    handleInputChange: (name: string, value: string | number) => void;
    showAge?: boolean;
}

const OTHER_PROVIDER = '__other__';
const knownProviders = ['EMDR UK', 'EMDR Europe'];

const today = new Date().toISOString().split('T')[0];

const ActivityEntry: React.FC<IActivityEntry> = ({ activity, index = -1, handleInputChange }) => {
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const markTouched = (name: string, value = true) =>
        setTouched((prev) => ({ ...prev, [name]: value }));

    const checkValidString = (input?: string) =>
        input !== undefined && input.trim().length > 0 && input !== OTHER_PROVIDER;
    const checkInvalidString = (input?: string) =>
        input !== undefined && (!input || input === OTHER_PROVIDER || input.trim().length === 0);

    const checkValid = (input?: Date | number) => input !== undefined && !!input;
    const checkInvalid = (input?: Date | number) => input !== undefined && !input;

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) =>
        handleInputChange(e.target.name, e.target.value);

    const handleOnChangeProvider = (e: ChangeEvent<HTMLInputElement>) => {
        handleOnChange(e);
        markTouched('specificEmdrCredits', false);
        markTouched('relatedCreditsHours', false);
        if (e.target.value === OTHER_PROVIDER) {
            handleInputChange('specificEmdrCredits', 0);
        } else {
            handleInputChange('relatedCreditsHours', 0);
        }
    };

    const handleOnChangeNumber = (e: ChangeEvent<HTMLInputElement>) =>
        handleInputChange(e.target.name, Number(e.target.value));

    const providerSelectValue = !activity?.provider
        ? ''
        : knownProviders.includes(activity.provider)
          ? activity.provider
          : OTHER_PROVIDER;

    // @ts-ignore
    return (
        <Fragment key={`activity-${index}`}>
            <Row className="mb-3 text-black">
                <Col xs={9}>
                    <FormGroup
                        key={`activity[${index}].eventAndTitle`}
                        controlId={`activity[${index}].eventAndTitle`}
                    >
                        <Form.Label className="h4">Event & Title</Form.Label>
                        <Form.Control
                            name="eventAndTitle"
                            value={activity?.eventAndTitle || ''}
                            required
                            onChange={handleOnChange}
                            onBlur={(e) => markTouched(e.target.name)}
                            autoFocus={true}
                            isValid={
                                touched.eventAndTitle && checkValidString(activity?.eventAndTitle)
                            }
                            isInvalid={
                                touched.eventAndTitle && checkInvalidString(activity?.eventAndTitle)
                            }
                        />
                    </FormGroup>
                </Col>

                <Col xs={3}>
                    <FormGroup
                        key={`activity[${index}].date`}
                        controlId={`activity[${index}].date`}
                    >
                        <Form.Label className={'h4'}>Date</Form.Label>
                        <Form.Control
                            name="date"
                            value={activity?.date || ''}
                            type="date"
                            max={today}
                            min="1950-01-01"
                            required
                            //@ts-ignore
                            onChange={handleOnChange}
                            onBlur={(e) => markTouched(e.target.name)}
                            isValid={touched.date && checkValidString(activity?.date)}
                            isInvalid={touched.date && checkInvalidString(activity?.date)}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row className="mb-3 text-black">
                <Col xs={12}>
                    <FormGroup
                        key={`activity[${index}].provider`}
                        controlId={`activity[${index}].provider`}
                    >
                        <Form.Label className="h4">Provider</Form.Label>
                        <Form.Select
                            name="provider"
                            value={providerSelectValue}
                            //@ts-ignore
                            onChange={handleOnChangeProvider}
                            onBlur={(e) => markTouched(e.target.name)}
                            required
                            isValid={touched.provider && checkValidString(activity?.provider)}
                            isInvalid={touched.provider && checkInvalidString(activity?.provider)}
                        >
                            <option value="">Select a provider</option>
                            <option value="EMDR UK">EMDR UK</option>
                            <option value="EMDR Europe">EMDR Europe</option>
                            <option value={OTHER_PROVIDER}>Other</option>
                        </Form.Select>

                        {providerSelectValue === OTHER_PROVIDER && (
                            <Form.Control
                                name="provider"
                                value={
                                    activity?.provider === OTHER_PROVIDER
                                        ? ''
                                        : activity?.provider || ''
                                }
                                onChange={(e) => handleInputChange('provider', e.target.value)}
                                placeholder="Enter provider"
                                required
                            />
                        )}
                    </FormGroup>
                </Col>
            </Row>
            <Row className="mb-3 text-black">
                <Col xs={9}>
                    <FormGroup
                        key={`activity[${index}].speakers`}
                        controlId={`activity[${index}].speakers`}
                    >
                        <Form.Label className="h4">Speakers</Form.Label>
                        <Form.Control
                            name="speakers"
                            value={activity?.speakers || ''}
                            onChange={handleOnChange}
                            onBlur={(e) => markTouched(e.target.name)}
                            required
                            isValid={touched.speakers && checkValidString(activity?.speakers)}
                            isInvalid={touched.speakers && checkInvalidString(activity?.speakers)}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row className="mb-3 text-black align-items-center">
                <Col xs={3}>
                    <FormGroup
                        key={`activity[${index}].relatedCreditsHours`}
                        controlId={`activity[${index}].relatedCreditsHours`}
                    >
                        <Form.Label className="h4">Related Credits/Hours</Form.Label>
                        <Form.Control
                            name="relatedCreditsHours"
                            value={activity?.relatedCreditsHours || ''}
                            onChange={handleOnChangeNumber}
                            onBlur={(e) => markTouched(e.target.name)}
                            type="number"
                            min={1}
                            disabled={providerSelectValue !== OTHER_PROVIDER}
                            required={providerSelectValue === OTHER_PROVIDER}
                            isValid={
                                touched.relatedCreditsHours &&
                                checkValid(activity?.relatedCreditsHours)
                            }
                            isInvalid={
                                touched.relatedCreditsHours &&
                                providerSelectValue === OTHER_PROVIDER &&
                                checkInvalid(activity?.relatedCreditsHours)
                            }
                        />
                    </FormGroup>
                </Col>
                <Col xs={1} className={'align-self-center text-center pt-4 fw-bold'}>
                    OR
                </Col>
                <Col xs={3}>
                    <FormGroup
                        key={`activity[${index}].specificEmdrCredits`}
                        controlId={`activity[${index}].specificEmdrCredits`}
                    >
                        <Form.Label className="h4">Specific EMDR Credits</Form.Label>
                        <Form.Control
                            name="specificEmdrCredits"
                            value={activity?.specificEmdrCredits || ''}
                            onChange={handleOnChangeNumber}
                            type="number"
                            min={1}
                            disabled={
                                activity?.provider !== 'EMDR UK' &&
                                activity?.provider !== 'EMDR Europe'
                            }
                            required={
                                activity?.provider === 'EMDR UK' ||
                                activity?.provider === 'EMDR Europe'
                            }
                            onBlur={(e) => markTouched(e.target.name)}
                            isValid={
                                touched.specificEmdrCredits &&
                                checkValid(activity?.specificEmdrCredits)
                            }
                            isInvalid={
                                touched.specificEmdrCredits &&
                                activity?.provider !== OTHER_PROVIDER &&
                                checkInvalid(activity?.specificEmdrCredits)
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>
        </Fragment>
    );
};

export default ActivityEntry;
